import * as React from "react";

import {
  BrowserRouter,
  StaticRouter,
  RouteProps,
  useLocation,
} from "react-router-dom";

import App from "./App";

import { AnalyticsProvider, nswTheme } from "@skv/core";
import type { PageEnvelope } from "@skv/core";

import { ThemeProvider, DefaultTheme } from "styled-components";

import {
  ServerStaticProvider,
  PageEnvelopeProvider,
  ReduxProvider,
  OrganisationSearchQueryProvider,
  OpportunitySearchQueryProvider,
  AppSettings,
  GlobalStaticProvider,
  GlobalStaticProps,
  RootViewProvider,
} from "@skv/state";

import {
  UserProvider,
  AuthenticatedUserState,
  EoiFormState,
  EoiFormProvider,
} from "~/state";

interface RootProps extends RouteProps {
  context: any; // ReactJS.NET -> ReactRouter
  pageEnvelope: PageEnvelope<unknown>;
  appSettings: AppSettings;
  global: GlobalStaticProps;
  user: AuthenticatedUserState;
  eoiForm: EoiFormState;
}

const RouterRootViewProvider: React.FC<{
  pageEnvelope: PageEnvelope<unknown>;
}> = ({ pageEnvelope, children }) => {
  const location = useLocation();

  return (
    <RootViewProvider
      initialState={{
        cache: {
          [`${location.pathname}${location.search}`]: pageEnvelope?.data,
        },
      }}
    >
      {children}
    </RootViewProvider>
  );
};

/* Server Entry, sorts out initial redux state */
const Root: React.FC<RootProps> = (props) => {
  const {
    location,
    context,
    pageEnvelope,
    global,
    appSettings,
    user,
    eoiForm,
  } = props;

  if (process.env.NODE_ENV === "development") {
    console.log(props);
    // eslint-disable-next-line
    React.useEffect(() => {
      window["__ROOT__"] = props;
    }, []);
  }

  const app = (
    <RouterRootViewProvider pageEnvelope={pageEnvelope}>
      <EoiFormProvider initialState={eoiForm || {}}>
        <GlobalStaticProvider value={global}>
          <AnalyticsProvider
            country="au"
            division="vol"
            site="nswgov"
            scriptSrc={appSettings.omnitureScriptUrl}
          >
            <ThemeProvider theme={nswTheme as DefaultTheme}>
              <ReduxProvider initialPageEnvelope={pageEnvelope}>
                <ServerStaticProvider appSettings={appSettings}>
                  <UserProvider user={user}>
                    <PageEnvelopeProvider initialPageEnvelope={pageEnvelope}>
                      <OpportunitySearchQueryProvider>
                        <OrganisationSearchQueryProvider>
                          <App />
                        </OrganisationSearchQueryProvider>
                      </OpportunitySearchQueryProvider>
                    </PageEnvelopeProvider>
                  </UserProvider>
                </ServerStaticProvider>
              </ReduxProvider>
            </ThemeProvider>
          </AnalyticsProvider>
        </GlobalStaticProvider>
      </EoiFormProvider>
    </RouterRootViewProvider>
  );

  if (typeof window === "undefined") {
    return (
      <StaticRouter context={context} location={location}>
        {app}
      </StaticRouter>
    );
  }

  return <BrowserRouter>{app}</BrowserRouter>;
};

export default Root;
